<template>
    <Head title="Edit Notification & Routing Policy" />

    <div class="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between">
                    <div>
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Edit Notification & Routing Policy</h2>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Update notification and routing policy settings</p>
                    </div>
                    <Link
                        :href="route('settings.tickets.notification-policies.index')"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                        Back
                    </Link>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6">
                <div class="space-y-8">
                    <!-- Section 1: Basic Information -->
                    <div>
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Basic Information
                        </h3>
                        <div class="space-y-4">
                            <!-- Name -->
                            <div>
                                <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Policy Name <span class="text-red-500 dark:text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    v-model="form.name"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.name }"
                                />
                                <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                            </div>

                            <!-- Description -->
                            <div>
                                <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                                <textarea
                                    id="description"
                                    v-model="form.description"
                                    rows="3"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <!-- Section 2: Matching Criteria -->
                    <div class="bg-gray-50 dark:bg-gray-700 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Matching Criteria
                        </h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Define which tickets should trigger this notification policy. Leave fields empty to match all tickets.
                        </p>
                        <div class="space-y-4">
                            <!-- Category -->
                            <div>
                                <label for="category_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                                <select
                                    id="category_id"
                                    v-model="form.category_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">All Categories</option>
                                    <option v-for="category in categories" :key="category.id" :value="category.id">
                                        {{ category.name }}
                                    </option>
                                </select>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Leave empty to apply to all categories</p>
                            </div>

                            <!-- Priority -->
                            <div>
                                <label for="priority" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
                                <select
                                    id="priority"
                                    v-model="form.priority"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">All Priorities</option>
                                    <option value="low">Low</option>
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Leave empty to apply to all priorities</p>
                            </div>

                            <!-- Location -->
                            <div>
                                <label for="location_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Location</label>
                                <select
                                    id="location_id"
                                    v-model="form.location_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">All Locations</option>
                                    <option v-for="location in locations" :key="location.id" :value="location.id">
                                        {{ location.name }}
                                    </option>
                                </select>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Leave empty to apply to all locations</p>
                            </div>

                            <!-- IT Equipment -->
                            <div v-if="$page.props.subscriptionFeatures.equipment_module">
                                <label for="it_equipment_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">IT Equipment</label>
                                <select
                                    id="it_equipment_id"
                                    v-model="form.it_equipment_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">All IT Equipment</option>
                                    <option v-for="equipment in itEquipment" :key="equipment.id" :value="equipment.id">
                                        {{ equipment.name }}
                                    </option>
                                </select>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Leave empty to apply to all IT equipment</p>
                            </div>
                        </div>
                    </div>

                    <!-- Section 3: Notification Recipients -->
                    <div>
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Notification Recipients
                        </h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Select users or roles who should receive notifications when tickets match this policy.
                        </p>
                        <div class="space-y-4">
                            <!-- Recipient Type -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Recipient Type <span class="text-red-500 dark:text-red-400">*</span>
                                </label>
                                <div class="mt-2 space-y-2">
                                    <label class="inline-flex items-center mr-6">
                                        <input
                                            type="radio"
                                            v-model="form.recipient_type"
                                            value="users"
                                            class="rounded-full border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span class="ml-2 text-gray-700 dark:text-gray-300">Specific Users</span>
                                    </label>
                                    <label class="inline-flex items-center">
                                        <input
                                            type="radio"
                                            v-model="form.recipient_type"
                                            value="roles"
                                            class="rounded-full border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span class="ml-2 text-gray-700 dark:text-gray-300">Roles</span>
                                    </label>
                                </div>
                            </div>

                            <!-- Users Selection -->
                            <div v-if="form.recipient_type === 'users'">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Users <span class="text-red-500 dark:text-red-400">*</span>
                                </label>
                                <div class="mt-2 max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md p-3">
                                    <label v-for="user in users" :key="user.id" class="flex items-center py-2">
                                        <input
                                            type="checkbox"
                                            :value="user.id"
                                            v-model="form.recipient_ids"
                                            class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span class="ml-2 text-gray-700 dark:text-gray-300">{{ user.name }} ({{ user.email }})</span>
                                    </label>
                                </div>
                                <p v-if="form.errors.recipient_ids" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.recipient_ids }}</p>
                            </div>

                            <!-- Roles Selection -->
                            <div v-if="form.recipient_type === 'roles'">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select Roles <span class="text-red-500 dark:text-red-400">*</span>
                                </label>
                                <div class="mt-2 max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md p-3">
                                    <label v-for="role in roles" :key="role.id" class="flex items-center py-2">
                                        <input
                                            type="checkbox"
                                            :value="role.id"
                                            v-model="form.recipient_ids"
                                            class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span class="ml-2 text-gray-700 dark:text-gray-300">{{ role.name }}</span>
                                    </label>
                                </div>
                                <p v-if="form.errors.recipient_ids" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.recipient_ids }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Section 4: Ticket Routing -->
                    <div class="bg-gray-50 dark:bg-gray-700 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Ticket Routing
                        </h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            Route tickets to specific Helpdesk Managers for triage and assignment. Only selected managers will see these tickets in their queue.
                        </p>

                        <div class="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-md p-3 mb-4">
                            <p class="text-sm text-yellow-800 dark:text-yellow-200">
                                <strong>Note:</strong> This is only for routing.
                                If you want these users to also receive notifications,
                                you must add them to the Notification Recipients section above.
                            </p>
                        </div>

                        <div class="space-y-2">
                            <div v-if="helpdeskManagerUsers.length === 0" class="text-sm text-gray-500 dark:text-gray-400 italic">
                                No Helpdesk Managers found. Users must have the 'helpdesk_manager' role to be available for routing.
                            </div>
                            <div v-else>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select Helpdesk Managers
                                </label>
                                <div class="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md p-3">
                                    <label v-for="user in helpdeskManagerUsers" :key="user.id" class="flex items-center py-2">
                                        <input
                                            type="checkbox"
                                            :value="user.id"
                                            v-model="form.routing_ids"
                                            class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span class="ml-2 text-gray-700 dark:text-gray-300">{{ user.name }} ({{ user.email }})</span>
                                    </label>
                                </div>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Leave empty to make tickets visible to all Helpdesk Managers
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Active Status -->
                    <div class="flex items-center">
                        <input
                            type="checkbox"
                            id="is_active"
                            v-model="form.is_active"
                            class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <label for="is_active" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                            Active
                        </label>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="mt-8 flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Link
                        :href="route('settings.tickets.notification-policies.index')"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 border border-transparent rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {{ form.processing ? 'Updating...' : 'Update Policy' }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
    policy: Object,
    categories: Array,
    roles: Array,
    users: Array,
    locations: Array,
    itEquipment: Array,
    helpdeskManagerUsers: Array,
});

const toast = useToast();

const form = useSecureForm({
    name: props.policy.name || '',
    description: props.policy.description || '',
    category_id: props.policy.category_id || '',
    priority: props.policy.priority || '',
    location_id: props.policy.location_id || '',
    it_equipment_id: props.policy.it_equipment_id || '',
    recipient_type: props.policy.recipient_type || 'users',
    recipient_ids: props.policy.recipient_ids || [],
    routing_ids: props.policy.routing_ids || [],
    is_active: props.policy.is_active ?? true,
});

const submit = () => {
    form.put(route('settings.tickets.notification-policies.update', props.policy.id), {
        onSuccess: () => {
            toast.success('Notification policy updated successfully!');
        },
        onError: () => {
            toast.error('Failed to update notification policy.');
        },
    });
};
</script>
